
import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, Menu, CheckCircle2, AlertCircle, X, ArrowRight, Shield, Facebook, Loader, ChevronRight, UserCircle, LogOut, CreditCard, FileText, Eye, EyeOff } from 'lucide-react';
import { AuthContext, ToastContext } from '../App';
import { ToastState } from '../types';
import { FACEBOOK_URL } from '../constants';
import { Z_INDEX } from '../constants/zIndex';
import { invalidateApiCache } from '../api';
import Modal from './Modal';

interface LayoutProps {
  children: React.ReactNode;
}

const LogoIcon = () => (
  <svg viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" role="img" aria-labelledby="logoTitle">
    <title id="logoTitle">Lorena Cleaning Services Logo</title>
    <defs>
      <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: '#38bdf8', stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: '#0284c7', stopOpacity: 1 }} />
      </linearGradient>
    </defs>
    <rect width="512" height="512" rx="256" fill="#F0F9FF" />
    <path 
      d="M256 96C256 96 112 264 112 352C112 431.529 176.471 496 256 496C335.529 496 400 431.529 400 352C400 264 256 96 256 96Z" 
      fill="url(#grad1)" 
    />
    <path 
      d="M192 340C192 340 205 325 240 325" 
      stroke="white" 
      strokeWidth="16" 
      strokeLinecap="round" 
      strokeOpacity="0.4" 
    />
    <path 
      d="M336 240L348 268L376 280L348 292L336 320L324 292L296 280L324 268L336 240Z" 
      fill="white" 
    />
    <path 
      d="M160 200L168 220L188 228L168 236L160 256L152 236L132 228L152 220L160 200Z" 
      fill="#7DD3FC" 
    />
  </svg>
);

const Logo = () => {
  return (
    <div className="flex items-center gap-4 group">
      <div className="relative">
        <div className="w-12 h-12 rounded-[1.25rem] shadow-xl shadow-sky-100 group-hover:scale-105 group-hover:rotate-6 transition-all duration-500 border-2 border-white bg-white overflow-hidden">
             <LogoIcon />
        </div>
        <div className="absolute -top-1 -right-1 bg-white rounded-full p-1 shadow-sm">
          <Sparkles className="h-3 w-3 text-sky-500" aria-hidden="true" />
        </div>
      </div>
      <div className="flex flex-col">
        <span className="font-extrabold text-slate-900 leading-none tracking-tight text-xl uppercase italic">Lorena's</span>
        <span className="text-[9px] text-sky-500 font-extrabold uppercase tracking-[0.3em] mt-0.5">Pristine Care</span>
      </div>
    </div>
  );
};

const Layout: React.FC<LayoutProps & { toast: ToastState }> = ({ children, toast }) => {
  const { user, firebaseUser, login, signup, logout, sendPasswordResetEmail, handleResendEmail, refreshVerificationStatus, resendVerificationCooldownSeconds } = useContext(AuthContext)!;
  const { showToast, dismissToast } = useContext(ToastContext)!;
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Toggle password visibility in login / signup modals (only one open at a time). */
  const [showPassword, setShowPassword] = useState(false);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);

  useEffect(() => {
    const handler = () => setSessionExpiredOpen(true);
    window.addEventListener("lorena:api-unauthorized", handler);
    return () => window.removeEventListener("lorena:api-unauthorized", handler);
  }, []);

  const handleSessionExpiredSignIn = () => {
    setSessionExpiredOpen(false);
    invalidateApiCache();
    logout();
    setIsLoginModalOpen(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      setShowStickyCTA(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleAuthAction = async (action: 'login' | 'signup' | 'reset') => {
    setIsSubmitting(true);
    try {
      if (action === 'login') {
        await login(email, password);
        setIsLoginModalOpen(false);
        setEmail('');
        setPassword('');
        navigate('/portal');
      } else if (action === 'signup') {
        const fullName = [firstName.trim(), middleName.trim(), lastName.trim()].filter(Boolean).join(' ');
        const result = await signup(fullName, email, password, phone, address);
        setIsSignupModalOpen(false);
        setEmail('');
        setPassword('');
        setPhone('');
        setAddress('');
        setFirstName('');
        setMiddleName('');
        setLastName('');
        if (result && 'redirectToVerifyEmail' in result && result.redirectToVerifyEmail) {
          navigate('/verify-email');
        } else {
          navigate('/portal');
        }
      } else if (action === 'reset') {
        await sendPasswordResetEmail(email);
        setIsResetModalOpen(false);
        setIsLoginModalOpen(false);
        setEmail('');
      }
    } catch (e: unknown) {
        showToast(e instanceof Error ? e.message : "An error occurred", "error");
    } finally {
        setIsSubmitting(false);
    }
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Our Story', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <div className="min-h-screen flex flex-col font-sans selection:bg-sky-100 selection:text-sky-600">
      {/* Toast Notification */}
      <div 
        role="alert"
        aria-live="polite"
        style={{ zIndex: Z_INDEX.toast }}
        className={`fixed top-8 left-1/2 -translate-x-1/2 flex items-center gap-4 px-6 sm:px-8 py-4 rounded-[2rem] bg-slate-900 text-white shadow-[0_30px_60px_-15px_rgba(15,23,42,0.4)] transition-all duration-700 cubic-bezier(0.175, 0.885, 0.32, 1.275) transform max-w-[min(100vw-2rem,42rem)] ${toast.isVisible ? 'translate-y-0 opacity-100' : '-translate-y-32 opacity-0'}`}
      >
        <div className={`shrink-0 p-1.5 rounded-full ${toast.type === 'error' ? 'bg-red-500' : toast.type === 'info' ? 'bg-amber-500' : 'bg-sky-500'}`}>
          {toast.type === 'error' ? <AlertCircle className="h-4 w-4 text-white" aria-hidden="true" /> : <CheckCircle2 className="h-4 w-4 text-white" aria-hidden="true" />}
        </div>
        <span className="text-sm font-extrabold uppercase tracking-widest min-w-0 flex-1">{toast.message}</span>
        {toast.undo?.onUndo && toast.isVisible ? (
          <button
            type="button"
            onClick={() => {
              void Promise.resolve(toast.undo!.onUndo()).finally(() => dismissToast());
            }}
            className="shrink-0 text-xs font-black uppercase tracking-widest text-sky-300 hover:text-white underline-offset-2 hover:underline px-2 py-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            Undo
          </button>
        ) : null}
      </div>

      {/* Navbar */}
      <header className="fixed top-0 left-0 right-0 transition-all duration-500 px-4 sm:px-6 py-3 flex justify-center pointer-events-none" style={{ zIndex: Z_INDEX.chrome }}>
        <nav
          className={`pointer-events-auto transition-all duration-500 rounded-[2rem] sm:rounded-[2.5rem] px-4 sm:px-8 py-2.5 sm:py-3 flex items-center justify-between gap-4 sm:gap-12 max-w-7xl w-full ${
            isScrolled ? 'glass-nav shadow-[0_20px_50px_-10px_rgba(15,23,42,0.1)]' : 'bg-transparent'
          }`}
          aria-label="Main Navigation"
        >
          <Link to="/" className="flex items-center" aria-label="Go to homepage">
            <Logo />
          </Link>

          <div className="hidden md:flex items-center gap-2">
            {navLinks.map(link => (
              <Link 
                key={link.path}
                to={link.path} 
                aria-current={location.pathname === link.path ? 'page' : undefined}
                className={`px-6 py-2.5 text-xs font-extrabold uppercase tracking-[0.15em] transition-all duration-200 ease-in-out rounded-2xl hover:bg-sky-50 hover:text-sky-600 ${location.pathname === link.path ? 'text-sky-600' : 'text-slate-500'}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-5">
            {user ? (
               <div className="hidden md:flex items-center gap-4">
                 {firebaseUser && !firebaseUser.emailVerified && (
                   <button
                     type="button"
                     onClick={() => { setIsLoginModalOpen(true); }}
                     className="text-xs font-extrabold uppercase tracking-[0.2em] text-amber-600 px-4 py-2.5 rounded-xl bg-amber-50 hover:bg-amber-100 transition-all duration-200 ease-in-out"
                   >
                     Verify email
                   </button>
                 )}
                 <Link to="/portal" className="text-xs font-extrabold uppercase tracking-[0.2em] text-sky-600 px-6 py-3 rounded-2xl bg-sky-50 hover:bg-sky-100 transition-all duration-200 ease-in-out">Dashboard</Link>
                 <button onClick={logout} className="p-3 rounded-xl text-slate-400 hover:text-slate-900 transition-all duration-200 ease-in-out" aria-label="Log out"><LogOut className="h-5 w-5" /></button>
               </div>
            ) : (
              <>
                <button onClick={() => setIsLoginModalOpen(true)} className="hidden md:block text-xs font-extrabold uppercase tracking-[0.2em] text-slate-500 hover:text-sky-600 transition-all duration-200 ease-in-out">Sign In</button>
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="md:hidden text-[11px] font-extrabold uppercase tracking-[0.18em] text-sky-600 bg-sky-50 hover:bg-sky-100 px-4 py-2.5 rounded-xl transition-all duration-200 ease-in-out"
                >
                  Sign In
                </button>
              </>
            )}
            <button
              onClick={() => (user ? navigate('/portal') : setIsLoginModalOpen(true))}
              className="btn-primary text-white text-[11px] font-extrabold uppercase tracking-[0.18em] px-5 py-3 min-h-[48px] rounded-2xl shadow-xl shadow-sky-100 inline-flex"
            >
              Book Now
            </button>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(true)}
              className="inline-flex h-11 min-h-[44px] min-w-[44px] touch-manipulation items-center justify-center rounded-2xl border border-slate-100 bg-white/70 p-2.5 text-slate-600 shadow-sm md:hidden"
              aria-label="Open mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <div 
        style={{ zIndex: Z_INDEX.navOverlay }}
        className={`fixed inset-0 flex min-h-0 flex-col overflow-hidden bg-slate-900 px-4 pb-[max(2.5rem,env(safe-area-inset-bottom))] pt-[max(1.5rem,env(safe-area-inset-top))] transition-transform duration-500 ease-in-out sm:px-8 sm:pb-12 sm:pt-10 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-label="Mobile Navigation Menu"
      >
        <div className="pointer-events-none absolute right-0 top-0 p-24 opacity-5 sm:p-32" aria-hidden="true">
            <Sparkles className="h-[min(600px,120vw)] w-[min(600px,120vw)] text-white" />
        </div>
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(false)}
          className="z-20 flex h-11 min-h-[44px] min-w-[44px] shrink-0 touch-manipulation items-center justify-center self-end rounded-2xl text-white/50 transition-colors hover:bg-white/10 hover:text-white"
          aria-label="Close mobile menu"
        >
          <X className="h-8 w-8" />
        </button>
        <div className="relative z-10 mt-6 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-y-contain sm:mt-12 sm:gap-8">
          {navLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className="min-h-[48px] touch-manipulation py-2 text-2xl font-extrabold tracking-tighter text-white transition-all duration-200 ease-in-out hover:text-sky-400 sm:text-3xl md:text-4xl"
            >
              {link.name}
            </Link>
          ))}
          {user ? (
            <>
              <Link
                to="/portal"
                onClick={() => setIsMobileMenuOpen(false)}
                className="min-h-[48px] touch-manipulation py-2 text-2xl font-extrabold tracking-tighter text-sky-400 transition-all duration-200 ease-in-out hover:text-sky-300 sm:text-3xl md:text-4xl"
              >
                Dashboard
              </Link>
              <button
                type="button"
                onClick={() => {
                  logout();
                  setIsMobileMenuOpen(false);
                }}
                className="flex min-h-[48px] touch-manipulation items-center gap-3 py-2 text-left text-2xl font-extrabold tracking-tighter text-white/80 transition-colors hover:text-sky-400 sm:text-3xl md:text-4xl"
              >
                <LogOut className="h-7 w-7 shrink-0 sm:h-8 sm:w-8" />
                Sign Out
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                setIsLoginModalOpen(true);
                setIsMobileMenuOpen(false);
              }}
              className="min-h-[48px] touch-manipulation py-2 text-left text-2xl font-extrabold tracking-tighter text-sky-400 sm:text-3xl md:text-4xl"
            >
              Sign In
            </button>
          )}
        </div>
        <div className="mt-6 shrink-0 border-t border-white/10 pt-6 sm:mt-auto sm:pt-10">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-white/30 mb-4">Lorena Cleaning Services LLC</p>
            <div className="flex gap-6">
                <a href="#" className="text-white/50 hover:text-white transition-colors" aria-label="Facebook"><Facebook className="h-6 w-6" /></a>
            </div>
        </div>
      </div>

      <main className="flex-grow">{children}</main>

      <footer className="bg-slate-50 pt-20 pb-12 border-t border-slate-100 relative overflow-hidden sm:pt-32 sm:pb-16">
          <div className="absolute top-0 left-0 w-full h-full bg-pattern pointer-events-none" aria-hidden="true"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 relative z-10">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-20 mb-24">
                  <div className="col-span-1 md:col-span-2">
                      <Logo />
                      <p className="mt-10 text-slate-500 max-w-sm text-lg leading-relaxed font-medium">
                          The most trusted name in premium home care. Transforming environments into sanctuaries since 2008.
                      </p>
                      <div className="mt-12 flex flex-wrap items-center gap-10">
                        <div className="flex items-center gap-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-[0.2em]">
                          <Shield className="h-5 w-5 text-sky-500" aria-hidden="true" /> Fully Insured
                        </div>
                        <div className="flex items-center gap-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-[0.2em]">
                          <CheckCircle2 className="h-5 w-5 text-sky-500" aria-hidden="true" /> Top Rated Service
                        </div>
                      </div>
                  </div>
                  <div>
                      <h4 className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-slate-900 mb-10">Navigation</h4>
                      <ul className="space-y-6 text-sm font-bold text-slate-500">
                          <li>
                              <Link to="/" className="hover:text-sky-600 transition-all duration-200 ease-in-out flex items-center gap-2 group py-1 inline-block w-full" onClick={(e) => { e.preventDefault(); navigate('/'); setTimeout(() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100); }}>
                                  Our Services <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-all duration-200 ease-in-out shrink-0" aria-hidden="true" />
                              </Link>
                          </li>
                          <li>
                              <Link to="/about" className="hover:text-sky-600 transition-all duration-200 ease-in-out flex items-center gap-2 group py-1 inline-block w-full" onClick={() => { navigate('/about'); window.scrollTo(0, 0); }}>
                                  The Method <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-all duration-200 ease-in-out shrink-0" aria-hidden="true" />
                              </Link>
                          </li>
                          <li>
                              <Link to="/contact" className="hover:text-sky-600 transition-all duration-200 ease-in-out flex items-center gap-2 group py-1 inline-block w-full" onClick={() => { navigate('/contact'); window.scrollTo(0, 0); }}>
                                  Coverage Map <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-all duration-200 ease-in-out shrink-0" aria-hidden="true" />
                              </Link>
                          </li>
                      </ul>
                  </div>
              </div>
              <div className="pt-12 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-8 text-[11px] font-extrabold text-slate-400 uppercase tracking-[0.2em]">
                  <div className="flex items-center gap-4">
                    <span>&copy; {new Date().getFullYear()} Lorena Cleaning Services LLC. Crafting purity with every visit.</span>
                  </div>
                  <div className="flex gap-10 items-center">
                    <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition-all hover:scale-125 flex items-center gap-3">
                      <Facebook className="h-6 w-6 fill-current" aria-label="Visit us on Facebook" /> <span className="hidden sm:inline">Follow Our Journey</span>
                    </a>
                  </div>
              </div>
          </div>
      </footer>

      <Modal
        isOpen={sessionExpiredOpen}
        onClose={() => setSessionExpiredOpen(false)}
        title="Session expired"
        size="sm"
        overlayZIndex={Z_INDEX.sessionExpiredModal}
      >
        <p className="text-sm text-slate-600 font-medium leading-relaxed mb-6">
          Your sign-in is no longer valid. Sign in again to continue. Draft text you were typing may still be saved on this device — use &quot;Restore&quot; on the booking form if you see it.
        </p>
        <button
          type="button"
          onClick={handleSessionExpiredSignIn}
          className="w-full py-4 bg-slate-900 text-white rounded-2xl font-extrabold text-xs uppercase tracking-widest hover:bg-black transition-colors"
        >
          Sign in again
        </button>
      </Modal>

      {/* Auth Modals */}
      <Modal isOpen={isLoginModalOpen} onClose={() => { setIsLoginModalOpen(false); setShowPassword(false); }} title="Welcome back to Lorena's" size="sm">
        <div className="space-y-6">
            <div className="space-y-2">
                <label htmlFor="loginEmail" className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-1">Email Address</label>
                <input
                  id="loginEmail"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="e.g. hello@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-h-[48px] w-full rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm font-medium outline-none transition-all focus:bg-white focus:ring-4 focus:ring-sky-100 sm:p-5"
                />
            </div>
            <div className="space-y-2">
                <div className="flex justify-between items-center px-1">
                    <label htmlFor="loginPass" className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Password</label>
                    <button type="button" onClick={() => { setIsLoginModalOpen(false); setIsResetModalOpen(true); }} className="text-[10px] font-extrabold text-sky-500 hover:text-sky-700 uppercase tracking-widest underline underline-offset-4">Forgot?</button>
                </div>
                <div className="relative">
                  <input
                    id="loginPass"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Your account password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="min-h-[48px] w-full rounded-2xl border border-slate-100 bg-slate-50 p-4 pr-14 text-sm font-medium outline-none transition-all focus:bg-white focus:ring-4 focus:ring-sky-100 sm:p-5"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 flex h-11 min-h-[44px] min-w-[44px] -translate-y-1/2 touch-manipulation items-center justify-center rounded-lg text-[#36454F] transition-colors hover:bg-slate-100/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 sm:right-4"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5 stroke-[1.5]" aria-hidden /> : <Eye className="h-5 w-5 stroke-[1.5]" aria-hidden />}
                  </button>
                </div>
            </div>
            <button onClick={() => handleAuthAction('login')} disabled={!email || !password || isSubmitting} className="w-full bg-slate-900 hover:bg-black text-white py-5 rounded-[2rem] font-extrabold text-xs uppercase tracking-[0.2em] shadow-2xl transition-all active:scale-[0.98] disabled:opacity-50 mt-4">
              {isSubmitting ? <Loader className="animate-spin h-5 w-5 mx-auto" /> : 'Confirm Identity'}
            </button>
            {firebaseUser != null && firebaseUser.emailVerified === false && (
              <div className="pt-4 space-y-3 text-center">
                <p className="text-[10px] font-extrabold text-slate-500 uppercase tracking-widest px-1">Verify your email to unlock all features.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={() => handleResendEmail()}
                    disabled={resendVerificationCooldownSeconds > 0}
                    className="flex-1 py-4 rounded-lg bg-sky-500 hover:bg-sky-600 text-white font-extrabold text-sm uppercase tracking-widest shadow-lg shadow-sky-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                  >
                    {resendVerificationCooldownSeconds > 0
                      ? `Resend in ${resendVerificationCooldownSeconds}s`
                      : 'Resend Verification Link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => refreshVerificationStatus()}
                    className="flex-1 py-4 rounded-lg border-2 border-slate-200 text-slate-700 font-extrabold text-xs uppercase tracking-widest hover:bg-slate-50 transition-all"
                  >
                    I already verified – Check again
                  </button>
                </div>
                <p className="text-xs text-slate-500 font-medium leading-relaxed px-1">
                  The app will automatically redirect you once you click the link in your email.
                </p>
              </div>
            )}
            <button type="button" onClick={() => { setShowPassword(false); setIsLoginModalOpen(false); setIsSignupModalOpen(true); }} className="w-full text-center text-xs font-extrabold text-slate-400 hover:text-sky-600 transition-colors uppercase tracking-widest">
              New client? <span className="text-sky-500 underline underline-offset-4">Register here</span>
            </button>
        </div>
      </Modal>

      <Modal isOpen={isSignupModalOpen} onClose={() => { setIsSignupModalOpen(false); setShowPassword(false); }} title="Join the Family" size="sm">
        <div className="space-y-5">
            <p className="text-sm text-slate-500 font-medium mb-4 px-1">Create an account to manage your custom schedule and pricing.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-2 sm:col-span-1">
                    <label htmlFor="signupFirstName" className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-1">First Name</label>
                    <input
                      id="signupFirstName"
                      name="given-name"
                      type="text"
                      autoComplete="given-name"
                      placeholder="Jane"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="min-h-[48px] w-full rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm font-medium outline-none transition-all focus:bg-white focus:ring-4 focus:ring-sky-100"
                    />
                </div>
                <div className="space-y-2 sm:col-span-1">
                    <label htmlFor="signupMiddleName" className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-1">Middle <span className="text-slate-300 font-normal">(optional)</span></label>
                    <input
                      id="signupMiddleName"
                      name="additional-name"
                      type="text"
                      autoComplete="additional-name"
                      placeholder="Marie"
                      value={middleName}
                      onChange={(e) => setMiddleName(e.target.value)}
                      className="min-h-[48px] w-full rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm font-medium outline-none transition-all focus:bg-white focus:ring-4 focus:ring-sky-100"
                    />
                </div>
                <div className="space-y-2 sm:col-span-1">
                    <label htmlFor="signupLastName" className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-1">Last Name</label>
                    <input
                      id="signupLastName"
                      name="family-name"
                      type="text"
                      autoComplete="family-name"
                      placeholder="Smith"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="min-h-[48px] w-full rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm font-medium outline-none transition-all focus:bg-white focus:ring-4 focus:ring-sky-100"
                    />
                </div>
            </div>
            <div className="space-y-2">
                <label htmlFor="signupEmail" className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-1">Email Address</label>
                <input
                  id="signupEmail"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="e.g. jane@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-h-[48px] w-full rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm font-medium outline-none transition-all focus:bg-white focus:ring-4 focus:ring-sky-100"
                />
            </div>
            <div className="space-y-2">
                <label htmlFor="signupPhone" className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-1">Phone Number</label>
                <input
                  id="signupPhone"
                  name="tel"
                  type="tel"
                  autoComplete="tel"
                  placeholder="215-000-0000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="min-h-[48px] w-full rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm font-medium outline-none transition-all focus:bg-white focus:ring-4 focus:ring-sky-100"
                />
            </div>
            <div className="space-y-2">
                <label htmlFor="signupAddress" className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-1">Home Address</label>
                <input
                  id="signupAddress"
                  name="street-address"
                  type="text"
                  autoComplete="street-address"
                  placeholder="Street, City, State, ZIP"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="min-h-[48px] w-full rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm font-medium outline-none transition-all focus:bg-white focus:ring-4 focus:ring-sky-100"
                />
            </div>
            <div className="space-y-2">
                <label htmlFor="signupPass" className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-1">Create Password</label>
                <div className="relative">
                  <input
                    id="signupPass"
                    name="new-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="min-h-[48px] w-full rounded-2xl border border-slate-100 bg-slate-50 p-4 pr-14 text-sm font-medium outline-none transition-all focus:bg-white focus:ring-4 focus:ring-sky-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 flex h-11 min-h-[44px] min-w-[44px] -translate-y-1/2 touch-manipulation items-center justify-center rounded-lg text-[#36454F] transition-colors hover:bg-slate-100/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5 stroke-[1.5]" aria-hidden /> : <Eye className="h-5 w-5 stroke-[1.5]" aria-hidden />}
                  </button>
                </div>
            </div>
            <button
              onClick={() => handleAuthAction('signup')}
              disabled={!firstName.trim() || !lastName.trim() || !email || !password || !address.trim() || isSubmitting}
              className="w-full bg-sky-500 hover:bg-sky-600 text-white py-5 rounded-[2rem] font-extrabold text-xs uppercase tracking-[0.2em] shadow-2xl shadow-sky-100 transition-all active:scale-[0.98] disabled:opacity-50 mt-6"
            >
              {isSubmitting ? <Loader className="animate-spin h-5 w-5 mx-auto" /> : 'Start Membership'}
            </button>
        </div>
      </Modal>

      <Modal isOpen={isResetModalOpen} onClose={() => setIsResetModalOpen(false)} title="Forgot Password?" size="sm">
        <div className="space-y-8">
            <p className="text-sm text-slate-500 font-medium leading-relaxed px-1">We'll send a secure reset link to your registered email address. This link will allow you to update your password externally.</p>
            <div className="space-y-2">
                <label htmlFor="resetEmail" className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-1">Your Email</label>
                <input
                  id="resetEmail"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="the@registered.email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-h-[48px] w-full rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm font-medium outline-none transition-all focus:bg-white focus:ring-4 focus:ring-sky-100 sm:p-5"
                />
            </div>
            <button onClick={() => handleAuthAction('reset')} disabled={!email || isSubmitting} className="w-full bg-slate-900 hover:bg-black text-white py-5 rounded-[2rem] font-extrabold text-xs uppercase tracking-[0.2em] shadow-2xl transition-all active:scale-[0.98] disabled:opacity-50">
              {isSubmitting ? <Loader className="animate-spin h-5 w-5 mx-auto" /> : 'Deliver Reset Link'}
            </button>
            <button type="button" onClick={() => { setShowPassword(false); setIsResetModalOpen(false); setIsLoginModalOpen(true); }} className="text-center text-xs font-extrabold text-slate-400 hover:text-sky-600 transition-colors uppercase tracking-widest w-full">
              Nevermind, I remember it
            </button>
        </div>
      </Modal>

      {/* Sticky CTA bar – appears on scroll for non-logged-in users */}
      {showStickyCTA && !user && location.pathname !== '/request-quote' && location.pathname !== '/contact' && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-slate-700 bg-slate-900 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-white shadow-2xl animate-in slide-in-from-bottom-4 duration-300 md:bottom-4 md:left-1/2 md:right-auto md:w-full md:max-w-md md:-translate-x-1/2 md:rounded-2xl md:border md:pb-3" style={{ zIndex: Z_INDEX.stickyCta }}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <p className="text-center text-sm font-bold sm:min-w-0 sm:flex-1 sm:truncate sm:text-left">Get your free quote.</p>
            <button
              type="button"
              onClick={() => navigate('/request-quote')}
              className="min-h-[44px] w-full touch-manipulation rounded-xl bg-sky-500 px-5 py-3 text-xs font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-sky-400 sm:w-auto sm:shrink-0 sm:py-2.5"
            >
              Get Quote
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
